import React, { useState } from 'react';
import { useAuth } from '../App';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
// import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { toast } from 'sonner';
import { X, Send, Save, XCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL.endsWith('/api') ? BACKEND_URL : `${BACKEND_URL}/api`;

const JobOfferModal = ({ isOpen, onClose, onSuccess, managerType = "general" }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    qualites_attendues: '',
    lieu_travail: '',
    date_arrivee: '',
    type_contrat: '',
    taux_horaire: ''
  });

  if (!isOpen) return null;

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (action) => {
    // Validation
    if (!formData.title.trim()) {
      toast.error('L\'intitulé du poste est requis');
      return;
    }
    if (!formData.description.trim()) {
      toast.error('La description des missions est requise');
      return;
    }
    if (!formData.qualites_attendues.trim()) {
      toast.error('Les qualités attendues sont requises');
      return;
    }
    if (!formData.lieu_travail.trim()) {
      toast.error('Le lieu de travail est requis');
      return;
    }
    if (!formData.date_arrivee) {
      toast.error('La date idéale d\'arrivée est requise');
      return;
    }
    if (!formData.type_contrat) {
      toast.error('Le type de contrat est requis');
      return;
    }

    // Additional validation for Horizon Solution
    if (managerType === "horizon") {
      if (!formData.taux_horaire || parseFloat(formData.taux_horaire) <= 0) {
        toast.error('Le taux horaire doit être un nombre positif');
        return;
      }
    }

    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const payload = {
        title: formData.title.trim(),
        description: formData.description.trim(),
        qualites_attendues: formData.qualites_attendues.trim(),
        lieu_travail: formData.lieu_travail.trim(),
        date_arrivee: formData.date_arrivee,
        type_contrat: formData.type_contrat, // Obligatoire pour tous les managers
        company: user?.company || (managerType === "horizon" ? "Horizon Solution" : "Ma Conciergerie"),
        status: action === 'send' ? 'en_attente' : 'brouillon'
      };

      // Add Horizon Solution specific fields
      if (managerType === "horizon") {
        payload.taux_horaire = parseFloat(formData.taux_horaire);
      }

      const response = await fetch(`${API}/job-offers`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const actionText = action === 'send' ? 'envoyée au service RH' : 'sauvegardée en brouillon';
        toast.success(`Offre d'emploi ${actionText} avec succès`);
        onSuccess();
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail || 'Erreur lors de la création de l\'offre');
      }
    } catch (error) {
      console.error('Error creating job offer:', error);
      toast.error('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    // Reset form
    setFormData({
      title: '',
      description: '',
      qualites_attendues: '',
      lieu_travail: '',
      date_arrivee: '',
      type_contrat: '',
      taux_horaire: ''
    });
    onClose();
  };

  return (
    <div className="job-offer-modal-overlay">
      <div className="job-offer-modal">
        <Card className="job-offer-modal-card">
          <CardHeader className="job-offer-modal-header">
            <div className="flex justify-between items-center">
              <CardTitle className="job-offer-modal-title">
                Créer une fiche de poste
              </CardTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleCancel}
                className="job-offer-modal-close"
              >
                <X size={20} />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="job-offer-modal-content">
            <form className="job-offer-form">
              {/* Intitulé du poste */}
              <div className="form-group">
                <Label htmlFor="title" className="form-label">
                  Intitulé du poste *
                </Label>
                <Input
                  id="title"
                  type="text"
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  className="form-input"
                  placeholder=""
                />
              </div>

              {/* Description des missions */}
              <div className="form-group">
                <Label htmlFor="description" className="form-label">
                  Description des missions *
                </Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  className="form-input"
                  rows={4}
                  placeholder=""
                />
              </div>

              {/* Qualités attendues */}
              <div className="form-group">
                <Label htmlFor="qualites" className="form-label">
                  Qualités attendues *
                </Label>
                <Textarea
                  id="qualites"
                  value={formData.qualites_attendues}
                  onChange={(e) => handleInputChange('qualites_attendues', e.target.value)}
                  className="form-input"
                  rows={3}
                  placeholder=""
                />
              </div>

              {/* Type de contrat (Tous les managers) */}
              <div className="form-group">
                <Label htmlFor="type_contrat" className="form-label">
                  Type de contrat *
                </Label>
                <select
                  id="type_contrat"
                  value={formData.type_contrat}
                  onChange={(e) => handleInputChange('type_contrat', e.target.value)}
                  className="form-input"
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '2px solid #e4e4e7',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    backgroundColor: 'white',
                    color: '#0a0a0a'
                  }}
                >
                  <option value="">Sélectionnez un type de contrat</option>
                  <option value="CDI">CDI</option>
                  <option value="CDD">CDD</option>
                  <option value="Stage">Stage</option>
                  <option value="Alternance">Alternance</option>
                  <option value="Freelance">Freelance</option>
                  <option value="Intérim">Intérim</option>
                </select>
              </div>

              {/* Lieu de travail */}
              <div className="form-group">
                <Label htmlFor="lieu" className="form-label">
                  Lieu de travail *
                </Label>
                <Input
                  id="lieu"
                  type="text"
                  value={formData.lieu_travail}
                  onChange={(e) => handleInputChange('lieu_travail', e.target.value)}
                  className="form-input"
                  placeholder=""
                />
              </div>

              {/* Date idéale d'arrivée */}
              <div className="form-group">
                <Label htmlFor="date_arrivee" className="form-label">
                  Date idéale d'arrivée *
                </Label>
                <Input
                  id="date_arrivee"
                  type="date"
                  value={formData.date_arrivee}
                  onChange={(e) => handleInputChange('date_arrivee', e.target.value)}
                  className="form-input"
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '2px solid #e4e4e7',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    backgroundColor: 'white',
                    color: '#0a0a0a'
                  }}
                />
              </div>

              {/* Taux horaire (Horizon Solution only) */}
              {managerType === "horizon" && (
                <div className="form-group">
                  <Label htmlFor="taux_horaire" className="form-label">
                    Taux horaire (en €) *
                  </Label>
                  <Input
                    id="taux_horaire"
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.taux_horaire}
                    onChange={(e) => handleInputChange('taux_horaire', e.target.value)}
                    className="form-input"
                    placeholder=""
                  />
                </div>
              )}

              {/* Action Buttons */}
              <div className="job-offer-modal-actions">
                <Button
                  type="button"
                  onClick={() => handleSubmit('send')}
                  disabled={loading}
                  className="btn-primary"
                  style={{ backgroundColor: '#eb5d2d', borderColor: '#eb5d2d' }}
                >
                  <Send size={16} className="mr-2" />
                  {loading ? 'Envoi...' : 'Envoyer'}
                </Button>
                <Button
                  type="button"
                  onClick={() => handleSubmit('save')}
                  disabled={loading}
                  variant="outline"
                  className="btn-outline"
                  style={{ borderColor: '#eb5d2d', color: '#eb5d2d' }}
                >
                  <Save size={16} className="mr-2" />
                  {loading ? 'Sauvegarde...' : 'Enregistrer'}
                </Button>
                <Button
                  type="button"
                  onClick={handleCancel}
                  disabled={loading}
                  variant="outline"
                  className="btn-cancel"
                >
                  <XCircle size={16} className="mr-2" />
                  Annuler
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default JobOfferModal;